/**
 * signOut — the one client-side sign-out path.
 *
 * Ends the server session, erases the client-side traces of the account, then
 * hard-navigates so no React state survives. It lives here because the two
 * call sites (site header, dashboard top bar) had drifted apart and neither
 * dropped the dashboard snapshot: sessionStorage outlives a same-tab
 * navigation, so the next person to sign in on that tab saw the previous
 * account's submissions and queued investor emails until the fetches landed.
 *
 * Anything a future sign-out entry point must do belongs in here, not next to
 * it — bypassing this helper means bypassing sign-out itself.
 */

import { getCsrfToken } from '@/lib/csrf';
import { SESSION_HINT_COOKIE } from '@/lib/session-hint';
import { clearShellSnapshot } from '@/components/dashboard/dashboardCache';

export async function signOut(): Promise<void> {
  try {
    await fetch('/api/auth/signout', {
      method: 'POST',
      headers: { 'X-CSRF-Token': getCsrfToken() || '' },
    });
  } catch {
    /* the local cleanup and the redirect have to happen either way */
  }

  try {
    localStorage.setItem('isAuthenticated', 'false');
    localStorage.setItem('isAdmin', 'false');
  } catch {
    /* storage disabled — the flags are only a first-paint hint */
  }

  clearShellSnapshot();

  // The proxy rewrites the hint on the next document request anyway; this
  // covers a bfcache restore, where no request is made at all.
  document.cookie = `${SESSION_HINT_COOKIE}=0; path=/; max-age=0; samesite=lax`;

  window.location.href = '/';
}
